/**
 * Color System - Central Export Point
 *
 * This is the single entry point for all color-related imports.
 *
 * @example
 * ```typescript
 * // Import the registry
 * import \{ colorRegistry, getColor, getColorsByTower \} from './colors';
 *
 * // Import tower overrides
 * import \{ towerOverrides, applyTowerOverride \} from './colors';
 *
 * // Import legacy colors (deprecated but still available)
 * import \{ brandColors, generalColors, tailwindColors \} from './colors';
 * ```
 */

// ============================================================================
// NEW: Color Registry (Solution 1 - Single Source of Truth)
// ============================================================================

// ============================================================================
// BACKGROUND THEMES (Generated from Registry)
// ============================================================================

import {colorRegistry} from './registry'

export {
	type ColorInfo,
	type ColorRegistry,
	type ColorClasses,
	type KontentColorOption,
	colorRegistry,
	getColor,
	getColorByAlias,
	getColorsByTower,
	getAllColors,
	// Brand support
	getColorByBrand,
	getColorsByBrand,
	getAllBrands,
	isBrandRegistered,
	// Tailwind CSS helpers
	getColorClasses,
	getHexCode,
	requireHexCode,
	// Kontent CMS helpers
	getColorsForCMS,
	getColorsForCMSByTower,
} from './registry'

// ============================================================================
// NEW: Naming Conventions (Solution 2 - Standardization)
// ============================================================================

export {
	NAMING_CONVENTIONS,
	isValidColorName,
	getColorNameValidationError,
	suggestColorName,
	parseColorName,
	generateColorName,
	isValidHexCode,
	normalizeHexCode,
	getHexCodeWithoutHash,
} from './naming-conventions'

// ============================================================================
// CONSOLIDATED COLOR EXPORTS (Single Source of Truth)
// ============================================================================
// All colors are now in registry.ts - these exports provide backward compatibility

export {
	brandColors,
	brandColorsMac,
	generalColors,
	tailwindColors,
	// Hex validation helpers (isValidHexCode is already exported from naming-conventions)
	validateRegistryHexCodes,
	getSafeHexCode,
} from './registry'

// ============================================================================
// NEW: TypeScript Color Types (IDE Autocomplete)
// ============================================================================

export {
	type ColorId,
	type TowerName,
	type ColorWithHex,
	type ColorHexMap,
	ALL_COLOR_IDS,
	ALL_TOWERS,
	ALL_BRANDS,
	isValidColorId,
	getTypedColor,
	getColorSafe,
	isValidTowerName,
	isRegisteredBrand,
	getColorHexMap,
} from './color-types'

// ============================================================================
// NEW: CSS Variables Generation
// ============================================================================

export {
	type CSSVariableSet,
	getCSSVariableName,
	generateCSSVariables,
	generateRootCSS,
	generateCompleteCSSFile,
	getColorStyle,
	getColorVar,
	hexToRGB,
} from './css-variables'

/**
 * Generate background theme class string for a color
 */
function generateThemeClass(
	colorId: string,
	textContrast: 'light' | 'dark'
): string {
	const bgClass = `bg-${colorId}`
	if (textContrast === 'dark') {
		return `${bgClass} text-black fill-black text-black-links`
	}
	return `${bgClass} text-white fill-white`
}

/**
 * Build background themes map from registry
 */
function buildBackgroundThemes(): Map<string, string> {
	const themes = new Map<string, string>()

	// Special non-registry themes (core design system colors)
	themes.set('none', '')
	themes.set('black', 'bg-black text-white fill-white')
	themes.set('white', 'bg-white text-black fill-black text-black-links')
	themes.set('midnight_blue', 'bg-midnight-blue text-white fill-white')
	themes.set('elanco_blue', 'bg-elanco-blue text-white fill-white')
	themes.set(
		'elanco_beige',
		'bg-elanco-beige text-black fill-black text-black-links'
	)
	themes.set('pet_green', 'bg-pet-green text-white fill-white')
	themes.set('vet_purple', 'bg-vet-purple text-white fill-white')
	themes.set(
		'elanco_green',
		'bg-elanco-green text-black fill-black text-black-links'
	)
	themes.set('elanco_grey', 'bg-elanco-grey text-white fill-white')
	themes.set(
		'light_blue',
		'bg-light-blue text-black fill-black text-black-links'
	)
	themes.set(
		'light_gray',
		'bg-light_gray text-black fill-black text-black-links'
	)
	themes.set(
		'mid_beige',
		'bg-mid-beige text-black fill-black text-black-links'
	)
	themes.set(
		'pet_green_light',
		'bg-pet-green-light text-black fill-black text-black-links'
	)
	themes.set(
		'purple_light',
		'bg-purple-light text-black fill-black text-black-links'
	)

	// Generate from registry - textContrast defaults to 'light' (white text)
	// Only add if not already in special themes (preserves special bg class names)
	for (const [id, color] of Object.entries(colorRegistry)) {
		if (!themes.has(id)) {
			const contrast: 'light' | 'dark' = color.textContrast ?? 'light'
			themes.set(id, generateThemeClass(id, contrast))
		}
	}

	return themes
}

/**
 * Background themes map - auto-generated from Color Registry
 * Use this for applying background colors with proper text contrast
 */
export const backgroundThemes = buildBackgroundThemes()

/**
 * Get background theme for a color ID
 * @param colorId - The color ID
 * @returns Theme class string or empty string if not found
 */
export function getBackgroundTheme(colorId: string): string {
	return backgroundThemes.get(colorId) ?? ''
}

// ============================================================================
// MIGRATION GUIDE
// ============================================================================
//
// OLD WAY (Deprecated):
// ────────────────────
// import { brandColors } from '@/colors/brand-colors';
// const color = brandColors.black.backgroundColor;
//
// NEW WAY (Recommended):
// ─────────────────────
// import { getColor } from '@/colors';
// const color = getColor('black')?.hexCode;
//
// TOWER-SPECIFIC COLORS:
// ──────────────────────
// import { getColorsByTower, applyTowerOverride } from '@/colors';
// const petColors = getColorsByTower('pet');
// const overriddenColor = applyTowerOverride('pet', baseColor);
